import * as THREE from 'three';
import { Evaluator, Brush, SUBTRACTION, ADDITION, INTERSECTION } from 'three-bvh-csg';

/**
 * Solid Geometry Kernel - BIM 2025 Grade
 * 
 * Responsible for high-precision boolean operations on 3D meshes.
 * Powered by 'three-bvh-csg' for robust constructive solid geometry.
 */
export class SolidKernel {

    // Re-use a single evaluator instance for performance
    private static evaluator = new Evaluator();

    /**
     * Perform a Union operation (A + B)
     */
    static union(meshA: THREE.Mesh, meshB: THREE.Mesh): THREE.Mesh {
        return this.performOperation(meshA, meshB, ADDITION);
    }

    /**
     * Perform a Subtract operation (A - B)
     */
    static subtract(meshA: THREE.Mesh, meshB: THREE.Mesh): THREE.Mesh {
        return this.performOperation(meshA, meshB, SUBTRACTION);
    }

    /**
     * Perform an Intersect operation (A & B)
     */
    static intersect(meshA: THREE.Mesh, meshB: THREE.Mesh): THREE.Mesh {
        return this.performOperation(meshA, meshB, INTERSECTION);
    }

    private static performOperation(meshA: THREE.Mesh, meshB: THREE.Mesh, op: any): THREE.Mesh {
        // Ensure matrices are updated
        meshA.updateMatrixWorld(true);
        meshB.updateMatrixWorld(true);

        // Create Brushes from meshes
        // Note: Brush clones the geometry, so we are safe from mutating originals
        const brushA = new Brush(meshA.geometry, meshA.material as THREE.Material);
        const brushB = new Brush(meshB.geometry, meshB.material as THREE.Material);

        // Sync transforms
        brushA.applyMatrix4(meshA.matrixWorld);
        brushB.applyMatrix4(meshB.matrixWorld);

        // Ensure BVH is built (Evaluator does this, but explicit update helps debugging)
        brushA.updateMatrixWorld(true);
        brushB.updateMatrixWorld(true);

        // Evaluate
        // useGroups: true preserves materials
        this.evaluator.useGroups = true;

        // Consolidate materials to prevent fragmentation in complex joins
        this.evaluator.consolidateMaterials = true;

        const result = this.evaluator.evaluate(brushA, brushB, op);

        // Reset transform dependent on the operation result context
        // The result is in World Space. We often want it in Local Space of A?
        // Usually, we want the result to replace A.
        // A was at position P.
        // Result is at (0,0,0) world, but vertices are transformed.

        // For simple replacement, we return the world-space mesh.
        // The consumer should handle parenting.

        // Cast shadow / Receive shadow transfer
        result.castShadow = meshA.castShadow;
        result.receiveShadow = meshA.receiveShadow;

        return result;
    }


    /**
     * Create a simple linear extrusion
     */
    static createExtrusion(shape: THREE.Shape, depth: number): THREE.Mesh {
        const geometry = new THREE.ExtrudeGeometry(shape, {
            depth: depth,
            bevelEnabled: false,
            curveSegments: 24
        });
        const mesh = new THREE.Mesh(geometry);
        mesh.updateMatrix();
        return mesh;
    }

    /**
     * Create a revolved solid (Lathe)
     * @param points - Points of the profile to revolve (Vector2)
     * @param axis - Axis of revolution (for now assumes Y axis, generic axis requires rotation)
     * @param angle - Angle of revolution (default 2PI)
     */
    static createRevolve(points: THREE.Vector2[], angle: number = Math.PI * 2): THREE.Mesh {
        const geometry = new THREE.LatheGeometry(points, 24, 0, angle);
        // Lathe revolves around Y axis.
        const mesh = new THREE.Mesh(geometry);
        mesh.updateMatrix();
        return mesh;
    }

    /**
     * Create a blend (Loft) between two profiles.
     * Assumes profiles are parallel and have same vertex count for simplicity.
     * Only supports Z-axis separation for now (like BIM Blend).
     */
    static createBlend(bottomProfile: THREE.Shape, topProfile: THREE.Shape, height: number): THREE.Mesh {
        // Custom geometry construction
        const geometry = new THREE.BufferGeometry();

        // 1. Get points
        const bottomPoints = bottomProfile.extractPoints(12).shape; // 12 segments
        const topPoints = topProfile.extractPoints(12).shape;

        // Ensure same count?
        // This is a naive implementation. Robust lofting is complex.
        // We'll proceed if counts match or force match?
        // For this clone, we'll just connect index-to-index.

        const vertices: number[] = [];
        const indices: number[] = [];

        // Add Base Vertices (z=0)
        bottomPoints.forEach(p => vertices.push(p.x, p.y, 0));
        // Add Top Vertices (z=height)
        topPoints.forEach(p => vertices.push(p.x, p.y, height));

        // Create side faces
        const N = bottomPoints.length;
        const M = topPoints.length; // Should equal N ideally
        const count = Math.min(N, M);

        for (let i = 0; i < count; i++) {
            const next = (i + 1) % count;
            // Quad: Bottom[i], Bottom[next], Top[next], Top[i]
            // Tri 1: B[i], B[next], T[i]
            // Tri 2: B[next], T[next], T[i] (CCW?)

            // Indices in vertices array:
            // Bottom: 0 to N-1
            // Top: N to N+M-1. 
            // Correct indexing:

            const b_i = i;
            const b_next = next;
            const t_i = N + i;
            const t_next = N + next;

            // Side 1
            indices.push(b_i, b_next, t_i);
            // Side 2
            indices.push(b_next, t_next, t_i);
        }

        // Cap (Top and Bottom) - Need triangulation
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const _bottomGeo = new THREE.ShapeGeometry(bottomProfile);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const _topGeo = new THREE.ShapeGeometry(topProfile);

        // We could merge geometries, but simpler to just produce sides for now?
        // No, need solid.
        // Let's rely on CSG or just return sides?
        // Better: Use ConvexHull? No.
        // Let's stick to returning just the sides + caps manually?

        geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
        geometry.setIndex(indices);
        geometry.computeVertexNormals();

        // Note: Missing Caps in this manual mesh. 
        // For a true solid, we need caps.
        // TODO: Implement Cap Triangulation merging.

        const mesh = new THREE.Mesh(geometry);
        return mesh;
    }

    /**
     * Create a swept blend (Sweep with varying profile).
     * Hard to implement perfectly without custom shader or advanced Geo.
     * Placeholder: Returns a simple sweep of the start profile.
     */
    static createSweptBlend(startProfile: THREE.Shape, endProfile: THREE.Shape, path: THREE.CurvePath<THREE.Vector3>): THREE.Mesh {
        // Fallback to simple sweep of start profile
        return this.createSweep(startProfile, path);
    }

    /**
     * Create a solid from a profile and path (Sweep)
     * Useful for Wall Sweeps, Railings, etc.
     */
    static createSweep(shape: THREE.Shape, path: THREE.CurvePath<THREE.Vector3>, segments: number = 20): THREE.Mesh {
        const geometry = new THREE.ExtrudeGeometry(shape, {
            extrudePath: path,
            steps: segments,
            bevelEnabled: false
        });
        const mesh = new THREE.Mesh(geometry);
        mesh.updateMatrix();
        return mesh;
    }

    /**
     * Check if two meshes intersect
     * Uses bounding box check first for performance.
     */
    static intersects(meshA: THREE.Mesh, meshB: THREE.Mesh): boolean {
        // 1. Fast Bounding Box Check
        // Ensure world matrices are up to date
        meshA.updateMatrixWorld(true);
        meshB.updateMatrixWorld(true);

        // Create OBB or AABB check?
        // AABB in world space is simplest first pass
        const boxA = new THREE.Box3().setFromObject(meshA);
        const boxB = new THREE.Box3().setFromObject(meshB);

        if (!boxA.intersectsBox(boxB)) {
            return false;
        }

        // 2. Precise CSG Intersection Check (Expensive)
        // Only do this if strictly needed. For now, Box check is often sufficient for "Potential Join".
        // TODO: Implement precise SAT or BVH overlap check if needed.

        return true;
    }

    /**
     * Apply a list of voids to a solid sequentially.
     */
    static applyVoids(solid: THREE.Mesh, voids: THREE.Mesh[]): THREE.Mesh {
        let result = solid;
        for (const voidMesh of voids) {
            // Check intersection first for performance
            if (this.intersects(result, voidMesh)) {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const _previous = result;
                result = this.subtract(result, voidMesh);
                // Clean up previous mesh if it wasn't the original?
                // In Three.js, geometry memory management is manual.
                // For now, we rely on GC or explicit dispose elsewhere.
            }
        }
        return result;
    }
}
